import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BookMarked, Save, Loader2, Sparkles, Plus, X } from "lucide-react";

function ListEditor({ label, hint, values, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(values || []), v]);
    setDraft("");
  };
  const remove = (idx) => onChange((values || []).filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-wider text-meta font-bold">{label}</p>
      {hint && <p className="text-[11px] text-meta-soft -mt-1">{hint}</p>}
      <div className="flex flex-wrap gap-1.5">
        {(values || []).map((v, i) => (
          <span key={i} className="pill pill-neutral inline-flex items-center gap-1.5" data-testid={`${testid}-item-${i}`}>
            {v}
            <button type="button" onClick={() => remove(i)} className="text-meta hover:text-danger" data-testid={`${testid}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder} data-testid={`${testid}-input`}
          className="flex-1 rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai transition" />
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="rounded-full bg-ai-soft text-ai px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 focus-ring">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, multiline, value, onChange, placeholder, testid, prefix }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] uppercase tracking-wider text-meta font-bold">{label}</span>
      {hint && <span className="block text-[11px] text-meta-soft">{hint}</span>}
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} data-testid={testid}
          className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-ai transition resize-none placeholder:text-meta-soft" />
      ) : (
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2 focus-within:border-ai transition">
          {prefix && <span className="text-xs text-meta shrink-0">{prefix}</span>}
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
            className="flex-1 min-w-0 bg-transparent text-sm text-ink outline-none placeholder:text-meta-soft" />
        </div>
      )}
    </label>
  );
}

export default function BrandMemory() {
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/nexus/brand");
      setBrand(r.data);
    } catch {
      toast.error("Could not load brand memory");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setBrand({ ...brand, [k]: v });
  const setVisual = (k, v) => setBrand({ ...brand, visual_kit: { ...(brand?.visual_kit || {}), [k]: v } });
  const setGoals = (k, v) => setBrand({ ...brand, revenue_goals: { ...(brand?.revenue_goals || {}), [k]: v ? Number(v) : null } });

  const save = async (e) => {
    e?.preventDefault?.();
    if (!brand) return;
    setBusy(true);
    try {
      const payload = {
        identity: brand.identity,
        positioning: brand.positioning,
        values: brand.values || [],
        tone_voice: brand.tone_voice,
        forbidden_phrases: brand.forbidden_phrases || [],
        signature_phrases: brand.signature_phrases || [],
        visual_kit: brand.visual_kit || {},
        revenue_goals: brand.revenue_goals || {},
      };
      const r = await api.put("/nexus/brand", payload);
      setBrand(r.data);
      toast.success("Brand memory saved · grounding all Workforce + Forge outputs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };

  if (!brand) return <div className="p-10 text-meta inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading brand memory…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px] mx-auto space-y-5" data-testid="nexus-brand-page">
      <div className="surface p-5 flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-ai-soft text-ai grid place-items-center"><BookMarked className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-bold text-ink">Brand Memory</h2>
          <p className="text-xs text-meta">Identity, positioning, voice and revenue goals — injected into every Forge generation, every Strategist brief, every Workforce reply.</p>
        </div>
        <span className="pill pill-ai shrink-0 hidden sm:inline-flex"><Sparkles className="h-3 w-3" /> Active</span>
      </div>

      <form onSubmit={save} className="grid lg:grid-cols-2 gap-5">
        <div className="surface p-5 space-y-4">
          <h3 className="font-display text-sm font-bold text-ink uppercase tracking-wider">Identity</h3>
          <Field label="Brand identity" hint="One paragraph: who you are, what you stand for" multiline
            value={brand.identity} onChange={(v) => set("identity", v)} testid="brand-identity"
            placeholder="A revenue operating system for solo creators who refuse to be reactive." />
          <Field label="Positioning" hint="One line — your sharpest statement"
            value={brand.positioning} onChange={(v) => set("positioning", v)} testid="brand-positioning"
            placeholder="The AI revenue team that compounds while you sleep." />
          <ListEditor label="Core values" hint="What the brand will never compromise on"
            values={brand.values} onChange={(v) => set("values", v)} placeholder="Operational clarity" testid="brand-values" />
        </div>

        <div className="surface p-5 space-y-4">
          <h3 className="font-display text-sm font-bold text-ink uppercase tracking-wider">Voice</h3>
          <Field label="Tone & voice" hint="How the brand sounds — paragraph form" multiline
            value={brand.tone_voice} onChange={(v) => set("tone_voice", v)} testid="brand-tone"
            placeholder="Direct, confident, no fluff. Speak to the founder-creator who reads at 6am." />
          <ListEditor label="Signature phrases" hint="Lines your Workforce should reach for"
            values={brand.signature_phrases} onChange={(v) => set("signature_phrases", v)} placeholder="while you sleep" testid="brand-signature" />
          <ListEditor label="Forbidden phrases" hint="Things your brand never says"
            values={brand.forbidden_phrases} onChange={(v) => set("forbidden_phrases", v)} placeholder="hustle harder" testid="brand-forbidden" />
        </div>

        <div className="surface p-5 space-y-4">
          <h3 className="font-display text-sm font-bold text-ink uppercase tracking-wider">Visual kit</h3>
          <Field label="Primary colour" value={brand.visual_kit?.primary_color} onChange={(v) => setVisual("primary_color", v)} placeholder="#7C3AED" testid="brand-color-primary" />
          <Field label="Accent colour" value={brand.visual_kit?.accent_color} onChange={(v) => setVisual("accent_color", v)} placeholder="#F97316" testid="brand-color-accent" />
          <Field label="Display font" value={brand.visual_kit?.font} onChange={(v) => setVisual("font", v)} placeholder="Tobias / Inter" testid="brand-font" />
        </div>

        <div className="surface p-5 space-y-4">
          <h3 className="font-display text-sm font-bold text-ink uppercase tracking-wider">Revenue goals</h3>
          <Field label="Monthly target ($)" value={brand.revenue_goals?.monthly_target ?? ""} onChange={(v) => setGoals("monthly_target", v)} placeholder="50000" testid="brand-goal-monthly" prefix="$" />
          <Field label="Annual target ($)" value={brand.revenue_goals?.annual_target ?? ""} onChange={(v) => setGoals("annual_target", v)} placeholder="750000" testid="brand-goal-annual" prefix="$" />
          <Field label="Cohort / launch size" value={brand.revenue_goals?.cohort_size ?? ""} onChange={(v) => setGoals("cohort_size", v)} placeholder="200" testid="brand-goal-cohort" />
        </div>

        <div className="lg:col-span-2 flex justify-end">
          <button type="submit" disabled={busy} data-testid="brand-save"
            className="rounded-full ashra-gradient-bg px-5 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60 focus-ring">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {busy ? "Saving…" : "Save brand memory"}
          </button>
        </div>
      </form>
    </div>
  );
}
